import { useState, useEffect, useCallback, useMemo, use } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useLoaderData } from 'react-router-dom';
import { Vendor } from '@/services/matching.service';

const defaultBannerImage = [
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/9a12dd564b695e4f16669d6f1d95d6db.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/8fa5cb6a05d6ee9b8c940b65e362d101.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/c06a4e33485578b4a247ab1c04884fd0.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/4baab42992fd67c5005e72b0818a8eb4.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/71717a8d56007665494e4cd54dca0e83.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/3cb24b7c32ce02bf560ce5a90ec6dc5a.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/bbfb57b2ca82759fa74757d286fd4249.jpg',
    },
    {
        image: 'https://d1vb419ubgm46b.cloudfront.net/PRD/202510/1ec44cb0629e58d04664ca1bcc181b24.jpg',
    },
];

const Banner2 = () => {
    const { vendor } = useLoaderData() as {
        vendor: Vendor | null;
    };
    const [bannerImages, setBannerImages] = useState<{ image: string; url?: string }[]>(defaultBannerImage);
    useEffect(() => {
        if (vendor && (vendor?.shop3Banner?.length || 0) > 0) setBannerImages(vendor.shop3Banner!);
        else setBannerImages(defaultBannerImage);
    }, [vendor]);

    useEffect(() => {
        // 다중 배너일 때는 1(첫 번째 실제 배너), 단일 배너일 때는 0
        setCurrentIndex(bannerImages.length > 1 ? 1 : 0);
    }, [bannerImages]);

    const [currentIndex, setCurrentIndex] = useState(0); // 초기값은 0으로 설정
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(true);

    const totalImages = bannerImages.length;
    const hasMultipleImages = totalImages > 1;

    // 무한 스크롤을 위한 확장된 배너 배열 생성
    const extendedBanners = useMemo(() => {
        if (!hasMultipleImages) return bannerImages;
        return [
            bannerImages[bannerImages.length - 1], // 마지막 배너 복제
            ...bannerImages, // 원본 배너들
            bannerImages[0], // 첫 번째 배너 복제
        ];
    }, [bannerImages, hasMultipleImages]);

    const totalExtendedImages = extendedBanners.length;

    // 다음 슬라이드로 이동
    const goToNext = useCallback(() => {
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => prev + 1);
    }, [hasMultipleImages]);

    // 이전 슬라이드로 이동
    const goToPrev = useCallback(() => {
        if (!hasMultipleImages) return;
        setCurrentIndex((prev) => prev - 1);
    }, [hasMultipleImages]);

    // 특정 인덱스로 이동 (실제 배너 인덱스 + 1)
    const goToIndex = useCallback(
        (index: number) => {
            if (!hasMultipleImages) return;
            setCurrentIndex(index + 1);
        },
        [hasMultipleImages],
    );

    // 자동 재생 기능
    useEffect(() => {
        if (!hasMultipleImages || !isAutoPlaying) return;

        const interval = setInterval(() => {
            goToNext();
        }, 4000);

        return () => clearInterval(interval);
    }, [hasMultipleImages, isAutoPlaying, goToNext]);

    // 터치 이벤트 핸들러
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setTouchStart(e.targetTouches[0].clientX);
        setTouchEnd(e.targetTouches[0].clientX);
    }, []);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    }, []);

    const handleTouchEnd = useCallback(() => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrev();
        }
    }, [touchStart, touchEnd, goToNext, goToPrev]);

    // 무한 스크롤 애니메이션 전환 로직
    useEffect(() => {
        if (!hasMultipleImages) return;

        // 마지막 복제본에 도달했을 때 (index: totalImages + 1)
        if (currentIndex >= totalImages + 1) {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
                setCurrentIndex(1); // 실제 첫 번째로 점프
                setTimeout(() => setIsTransitioning(true), 50);
            }, 500);
            return () => clearTimeout(timer);
        }

        // 첫 번째 복제본에 도달했을 때 (index: 0)
        if (currentIndex <= 0) {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
                setCurrentIndex(totalImages); // 실제 마지막으로 점프
                setTimeout(() => setIsTransitioning(true), 50);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [currentIndex, totalImages, hasMultipleImages]);

    // 키보드 네비게이션
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (!hasMultipleImages) return;

            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    goToPrev();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    goToNext();
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [hasMultipleImages, goToPrev, goToNext]);

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: 2000,
                mx: 'auto',
                p: { xs: '16px', sm: 1, md: '40px', lg: '40px' },
                mt: { xs: 2, md: 4 },
                '@media (max-width: 600px)': {
                    p: 0,
                    width: '100%',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: 'fit-content',
                    maxHeight: 500,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    backgroundColor: '#f5f5f5',
                    '@media (max-width: 600px)': {
                        borderRadius: 0,
                        maxHeight: 'none',
                    },
                }}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                role="region"
                aria-label="배너 슬라이더"
                tabIndex={0}
            >
                {/* 이미지 컨테이너 */}
                <Box
                    sx={{
                        display: 'flex',
                        width: hasMultipleImages ? `${totalExtendedImages * 100}%` : '100%',
                        height: 'fit-content',
                        transform: hasMultipleImages
                            ? `translateX(-${(currentIndex * 100) / totalExtendedImages}%)`
                            : 'translateX(0%)',
                        transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
                        willChange: 'transform',
                    }}
                >
                    {extendedBanners.map(({ image, url }, index) => (
                        <Box
                            key={`${image}-${index}`}
                            sx={{
                                width: hasMultipleImages ? `${100 / totalExtendedImages}%` : '100%',
                                height: '100%',
                                position: 'relative',
                                cursor: url ? 'pointer' : 'default',
                            }}
                        >
                            {url ? (
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                    <img
                                        src={image}
                                        alt={`배너 이미지 ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            display: 'block',
                                            aspectRatio: window.innerWidth <= 600 ? '16 / 7' : '1920 / 500',
                                        }}
                                        draggable={false}
                                        loading="lazy"
                                    />
                                </a>
                            ) : (
                                <img
                                    src={image}
                                    alt={`배너 이미지 ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        display: 'block',
                                        aspectRatio: window.innerWidth <= 600 ? '16 / 7' : '1920 / 500',
                                    }}
                                    draggable={false}
                                    loading="lazy"
                                />
                            )}
                        </Box>
                    ))}
                </Box>

                {/* 네비게이션 화살표 - 다중 이미지일 때만 표시 */}
                {hasMultipleImages && (
                    <>
                        {/* 이전 버튼 */}
                        <IconButton
                            onClick={goToPrev}
                            aria-label="이전 배너로 이동"
                            sx={{
                                position: 'absolute',
                                left: 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#333',
                                width: 44,
                                height: 44,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 2,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                    transform: 'translateY(-50%) scale(1.05)',
                                },
                                transition: 'all 0.2s ease',
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 20 }} />
                        </IconButton>

                        {/* 다음 버튼 */}
                        <IconButton
                            onClick={goToNext}
                            aria-label="다음 배너로 이동"
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                color: '#333',
                                width: 44,
                                height: 44,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                zIndex: 2,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                    transform: 'translateY(-50%) scale(1.05)',
                                },
                                transition: 'all 0.2s ease',
                                display: { xs: 'none', sm: 'flex' },
                            }}
                        >
                            <ArrowForwardIosIcon sx={{ fontSize: 20 }} />
                        </IconButton>

                        {/* 인디케이터 */}
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                gap: 1,
                                zIndex: 2,
                            }}
                        >
                            {(bannerImages?.length > 0 ? bannerImages : [defaultBannerImage]).map((_, index) => {
                                // 현재 실제 배너 인덱스 계산 (복제본 제외)
                                const actualIndex = (currentIndex - 1 + totalImages) % totalImages;
                                const isActive = index === actualIndex;

                                return (
                                    <Box
                                        key={index}
                                        onClick={() => goToIndex(index)}
                                        role="button"
                                        aria-label={`${index + 1}번째 배너로 이동`}
                                        aria-current={isActive ? 'true' : 'false'}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                goToIndex(index);
                                            }
                                        }}
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            backgroundColor: isActive ? '#fff' : 'rgba(255, 255, 255, 0.5)',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            '&:hover': {
                                                backgroundColor: '#fff',
                                                transform: 'scale(1.2)',
                                            },
                                            '&:focus': {
                                                outline: '2px solid #fff',
                                                outlineOffset: '2px',
                                            },
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    );
};

export default Banner2;
